import CryptoJS from 'crypto-js';
import process from 'node:process';

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
/**
 * Encrypts data using AES encryption.
 * @param {any} data - The data to be encrypted.
 * @returns {string} - The encrypted data as a string.
 */
const encryptData = (data: any): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

/**
 * Decrypts data using AES decryption.
 * @param {string} encryptedData - The data to be decrypted.
 * @returns {any} - The decrypted data.
 */
const decryptData = (encryptedData: string): any => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  try {
    return JSON.parse(decryptedData);
  } catch (error) {
    // If parsing fails, return the raw string value
    return decryptedData;
  }
};

/**
 * Retrieves the local data stored with the specified key.
 * @param {string} key - The key used to store the data.
 * @returns {Promise<any | null>} - A promise that resolves to the retrieved data or null if it doesn't exist.
 */
export const getLocalData = async (key: string): Promise<any | null> => {
  try {
    const jsonValue = localStorage.getItem(key);
    if (jsonValue) {
      try {
        return decryptData(jsonValue);
      } catch (err) {
        return null;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Stores the specified value in the local storage with the given key.
 * @param {string} key - The key used to store the data.
 * @param {any} value - The value to be stored.
 * @returns {Promise<boolean>} - A promise that resolves to true if the value is successfully stored, or rejects with an error if an error occurs.
 */
export const setLocalData = async (key: string, value: any): Promise<boolean> => {
  try {
    const encryptedValue = encryptData(value);
    localStorage.setItem(key, encryptedValue);
    return true;
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Clears all data stored in localStorage except for the specified key.
 * @param {string} preserveKey - The key that should not be removed.
 * @returns {Promise<boolean>} - A promise that resolves to true if successful, or false if an error occurs.
 */
export const flushLocalData = async (preserveKey: string): Promise<boolean> => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key !== preserveKey) {
        localStorage.removeItem(key);
      }
    });
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error('Error clearing localStorage:', error);
    }
    return false;
  }
};
